import type { AgentPublicConfig, Cta } from '@cloneai/shared-types';

export interface RuntimeLabels {
  readonly loading: string;
  readonly genericErrorTitle: string;
  readonly configLoadFailed: string;
  readonly retry: string;
  readonly notFoundTitle: string;
  readonly notFoundBody: string;
  readonly archivedTitle: string;
  readonly archivedBody: string;
  readonly pausedTitle: string;
  readonly pausedBody: (agentName: string) => string;
  readonly startSession: string;
  readonly retryHint: string;
  readonly connecting: string;
  readonly ended: string;
  readonly subtitles: string;
  readonly languageSelector: string;
  readonly switchToText: string;
  readonly textChatTitle: string;
  readonly textChatPlaceholder: string;
  readonly textChatSend: string;
  readonly textChatThinking: string;
  readonly textChatError: string;
  readonly playAnimation: string;
  readonly stopAnimation: string;
}

const EN_LABELS: RuntimeLabels = {
  loading: 'Loading',
  genericErrorTitle: 'Something went wrong',
  configLoadFailed: 'Unable to load this agent. Please try again.',
  retry: 'Retry',
  notFoundTitle: 'Agent not found',
  notFoundBody: "We couldn't find the agent you're looking for.",
  archivedTitle: 'Agent is no longer available',
  archivedBody: 'This agent has been archived.',
  pausedTitle: 'Temporarily unavailable',
  pausedBody: (agentName) => `${agentName} is temporarily unavailable. Please check back later.`,
  startSession: 'Start agent session',
  retryHint: 'The voice session could not start. Try again.',
  connecting: 'Connecting...',
  ended: 'Session ended',
  subtitles: 'Conversation captions',
  languageSelector: 'Language',
  switchToText: 'Text chat',
  textChatTitle: 'Text chat',
  textChatPlaceholder: 'Type your message...',
  textChatSend: 'Send',
  textChatThinking: 'Thinking...',
  textChatError: 'Unable to send. Try again.',
  playAnimation: 'Play animated response',
  stopAnimation: 'Stop animated response',
};

const LABELS: Record<string, RuntimeLabels> = {
  en: EN_LABELS,
  fr: {
    loading: 'Chargement',
    genericErrorTitle: 'Une erreur est survenue',
    configLoadFailed: 'Impossible de charger cet agent. Veuillez reessayer.',
    retry: 'Reessayer',
    notFoundTitle: 'Agent introuvable',
    notFoundBody: "Nous n'avons pas trouve l'agent demande.",
    archivedTitle: "L'agent n'est plus disponible",
    archivedBody: 'Cet agent a ete archive.',
    pausedTitle: 'Temporairement indisponible',
    pausedBody: (agentName) =>
      `${agentName} est temporairement indisponible. Veuillez revenir plus tard.`,
    startSession: "Demarrer la session de l'agent",
    retryHint: "La session vocale n'a pas pu demarrer. Veuillez reessayer.",
    connecting: 'Connexion...',
    ended: 'Session terminee',
    subtitles: 'Sous-titres de la conversation',
    languageSelector: 'Langue',
    switchToText: 'Clavarder',
    textChatTitle: 'Clavardage',
    textChatPlaceholder: 'Ecrivez votre message...',
    textChatSend: 'Envoyer',
    textChatThinking: 'Reponse en cours...',
    textChatError: "Impossible d'envoyer. Veuillez reessayer.",
    playAnimation: 'Lire la reponse animee',
    stopAnimation: 'Arreter la reponse animee',
  },
  es: {
    loading: 'Cargando',
    genericErrorTitle: 'Algo salio mal',
    configLoadFailed: 'No se pudo cargar este agente. Intentalo de nuevo.',
    retry: 'Reintentar',
    notFoundTitle: 'Agente no encontrado',
    notFoundBody: 'No pudimos encontrar el agente que buscas.',
    archivedTitle: 'El agente ya no esta disponible',
    archivedBody: 'Este agente fue archivado.',
    pausedTitle: 'Temporalmente no disponible',
    pausedBody: (agentName) =>
      `${agentName} no esta disponible temporalmente. Vuelve a intentarlo mas tarde.`,
    startSession: 'Iniciar sesion con el agente',
    retryHint: 'No se pudo iniciar la sesion de voz. Intentalo de nuevo.',
    connecting: 'Conectando...',
    ended: 'Sesion finalizada',
    subtitles: 'Subtítulos de la conversación',
    languageSelector: 'Idioma',
    switchToText: 'Chat de texto',
    textChatTitle: 'Chat de texto',
    textChatPlaceholder: 'Escribe tu mensaje...',
    textChatSend: 'Enviar',
    textChatThinking: 'Pensando...',
    textChatError: 'No se pudo enviar. Intentalo de nuevo.',
    playAnimation: 'Reproducir respuesta animada',
    stopAnimation: 'Detener respuesta animada',
  },
};

export function normalizeLanguageTag(value: string | null | undefined): string | null {
  if (!value) return null;
  const base = value.trim().toLowerCase().split('-')[0] ?? '';
  return /^[a-z]{2}$/.test(base) ? base : null;
}

export function languageStorageKey(tenantSlug: string, agentSlug: string): string {
  return `cloneai:agent-lang:${tenantSlug}:${agentSlug}`;
}

export function resolveInitialLanguage(
  config: AgentPublicConfig,
  options: {
    readonly queryLang?: string | null | undefined;
    readonly storedLang?: string | null | undefined;
    readonly browserLanguages?: readonly string[] | undefined;
  } = {},
): string {
  const supported = new Set(config.languages);
  const candidates = [
    options.queryLang,
    options.storedLang,
    ...(options.browserLanguages ?? []),
    config.defaultLang,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeLanguageTag(candidate);
    if (normalized && supported.has(normalized)) return normalized;
  }

  return config.languages[0] ?? config.defaultLang;
}

export function getRuntimeLabels(lang: string | null | undefined): RuntimeLabels {
  const normalized = normalizeLanguageTag(lang) ?? 'en';
  return LABELS[normalized] ?? EN_LABELS;
}

export function selectLocalizedConfig(
  config: AgentPublicConfig,
  lang: string | null,
): AgentPublicConfig {
  const selected = normalizeLanguageTag(lang);
  const localized = selected ? config.localized[selected] : undefined;
  if (!localized) return config;

  const ctas: Cta[] = localized.ctas.map((cta) => ({
    ...cta,
    content: {},
  }));

  return {
    ...config,
    name: localized.name,
    videoUrl: localized.videoUrl,
    hevcVideoUrl: localized.hevcVideoUrl ?? null,
    posterUrl: localized.posterUrl,
    ctas,
    defaultLang: localized.lang,
  };
}
